var __rest = this && this.__rest || function (s, e) {
  var t = {};

  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};

import React from 'react';
import cls from 'classnames';
import PropTypes from 'prop-types';
import { cssClasses, strings } from '@douyinfe/semi-foundation/lib/es/avatar/constants';
import AvatarFoundation from '@douyinfe/semi-foundation/lib/es/avatar/foundation';
import '@douyinfe/semi-foundation/lib/es/avatar/avatar.css';
import { noop } from '@douyinfe/semi-foundation/lib/es/utils/function';
import BaseComponent from '../_base/baseComponent';
import { handlePrevent } from '@douyinfe/semi-foundation/lib/es/utils/a11y';
const sizeSet = strings.SIZE;
const shapeSet = strings.SHAPE;
const colorSet = strings.COLOR;
const prefixCls = cssClasses.PREFIX;
export * from './interface';
export default class Avatar extends BaseComponent {
  constructor(props) {
    super(props);

    this.handleFocusVisible = event => {
      this.foundation.handleFocusVisible(event);
    };

    this.handleBlur = event => {
      this.foundation.handleBlur();
    };

    this.getContent = () => {
      const {
        children,
        onClick,
        imgAttr,
        src,
        srcSet,
        alt
      } = this.props;
      const {
        isImgExist
      } = this.state;
      let content = children;
      const clickable = onClick !== noop;
      const isImg = src && isImgExist;
      const a11yFocusProps = {
        tabIndex: 0,
        onKeyDown: this.handleKeyDown,
        onFocus: this.handleFocusVisible,
        onBlur: this.handleBlur
      };

      if (isImg) {
        const finalAlt = clickable ? "clickable Avatar: ".concat(alt) : alt;
        const imgBasicProps = Object.assign(Object.assign({
          src,
          srcSet,
          onError: this.handleError
        }, imgAttr), {
          className: cls({
            ["".concat(prefixCls, "-no-focus-visible")]: clickable
          })
        });
        const imgProps = clickable ? Object.assign(Object.assign({}, imgBasicProps), a11yFocusProps) : imgBasicProps;
        content = /*#__PURE__*/React.createElement("img", Object.assign({
          alt: finalAlt
        }, imgProps));
      } else if (typeof children === 'string') {
        const tempAlt = alt !== null && alt !== void 0 ? alt : children;
        const finalAlt = clickable ? "clickable Avatar: ".concat(tempAlt) : tempAlt;
        const props = {
          role: 'img',
          'aria-label': finalAlt,
          className: cls("".concat(prefixCls, "-label"), {
            ["".concat(prefixCls, "-no-focus-visible")]: clickable
          })
        };
        const finalProps = clickable ? Object.assign(Object.assign({}, props), a11yFocusProps) : props;
        content = /*#__PURE__*/React.createElement("span", {
          className: "".concat(prefixCls, "-content")
        }, /*#__PURE__*/React.createElement("span", Object.assign({}, finalProps, {
          "x-semi-prop": "children"
        }), children));
      }

      return content;
    };

    this.state = {
      isImgExist: true,
      hoverContent: '',
      focusVisible: false
    };
    this.onEnter = this.onEnter.bind(this);
    this.onLeave = this.onLeave.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.getContent = this.getContent.bind(this);
  }

  get adapter() {
    return Object.assign(Object.assign({}, super.adapter), {
      notifyImgState: isImgExist => {
        this.setState({
          isImgExist
        });
      },
      notifyEnter: e => {
        const {
          hoverMask
        } = this.props;
        const hoverContent = hoverMask;
        this.setState({
          hoverContent
        }, () => {
          const {
            onMouseEnter
          } = this.props;
          onMouseEnter && onMouseEnter(e);
        });
      },
      notifyLeave: e => {
        this.setState({
          hoverContent: ''
        }, () => {
          const {
            onMouseLeave
          } = this.props;
          onMouseLeave && onMouseLeave(e);
        });
      },
      setFocusVisible: focusVisible => {
        this.setState({
          focusVisible
        });
      }
    });
  }

  componentDidMount() {
    this.foundation = new AvatarFoundation(this.adapter);
    this.foundation.init();
  }

  componentDidUpdate(prevProps) {
    if (this.props.src && this.props.src !== prevProps.src) {
      const image = new Image(0, 0);
      image.src = this.props.src;

      image.onload = () => {
        this.setState({
          isImgExist: true
        });
      };

      image.onerror = () => {
        this.setState({
          isImgExist: false
        });
      };

      image.onabort = () => {
        this.setState({
          isImgExist: false
        });
      };
    }
  }

  componentWillUnmount() {
    this.foundation.destroy();
  }

  onEnter(e) {
    this.foundation.handleEnter(e);
  }

  onLeave(e) {
    this.foundation.handleLeave(e);
  }

  handleError() {
    this.foundation.handleImgLoadError();
  }

  handleKeyDown(event) {
    const {
      onClick
    } = this.props;

    switch (event.key) {
      case "Enter":
        onClick(event);
        handlePrevent(event);
        break;

      case 'Escape':
        event.target.blur();
        break;

      default:
        break;
    }
  }

  render() {
    // eslint-disable-next-line max-len, no-unused-vars
    const _a = this.props,
          {
      shape,
      children,
      size,
      color,
      className,
      hoverMask,
      onClick,
      imgAttr,
      src,
      srcSet,
      style,
      alt
    } = _a,
          others = __rest(_a, ["shape", "children", "size", "color", "className", "hoverMask", "onClick", "imgAttr", "src", "srcSet", "style", "alt"]);

    const {
      isImgExist,
      hoverContent,
      focusVisible
    } = this.state;
    const isImg = src && isImgExist;
    const avatarCls = cls(prefixCls, {
      ["".concat(prefixCls, "-").concat(shape)]: shape,
      ["".concat(prefixCls, "-").concat(size)]: size,
      ["".concat(prefixCls, "-").concat(color)]: color && !isImg,
      ["".concat(prefixCls, "-img")]: isImg,
      ["".concat(prefixCls, "-focus")]: focusVisible
    }, className);
    const hoverRender = hoverContent ? /*#__PURE__*/React.createElement("div", {
      className: "".concat(prefixCls, "-hover"),
      "x-semi-prop": "hoverContent"
    }, hoverContent) : null;
    return /*#__PURE__*/React.createElement("span", Object.assign({}, others, {
      style: style,
      className: avatarCls,
      onClick: onClick,
      onMouseEnter: this.onEnter,
      onMouseLeave: this.onLeave,
      role: 'listitem'
    }), this.getContent(), hoverRender);
  }

}
Avatar.defaultProps = {
  size: 'medium',
  color: 'grey',
  shape: 'circle',
  onClick: noop,
  onMouseEnter: noop,
  onMouseLeave: noop
};
Avatar.propTypes = {
  children: PropTypes.node,
  color: PropTypes.oneOf(colorSet),
  shape: PropTypes.oneOf(shapeSet),
  size: PropTypes.oneOf(sizeSet),
  hoverMask: PropTypes.node,
  className: PropTypes.string,
  style: PropTypes.object,
  imgAttr: PropTypes.object,
  src: PropTypes.string,
  srcSet: PropTypes.string,
  alt: PropTypes.string,
  onError: PropTypes.func,
  onClick: PropTypes.func,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func
};
Avatar.elementType = 'Avatar';